/** Procedural cues (no asset files). WP7 replaces these with a real SFX pack. */
export type Cue = 'wobble' | 'creak' | 'fire' | 'bonus' | 'bonusEnd' | 'thud' | 'coins' | 'bonk' | 'ping' | 'gem' | 'step' | 'mutate' | 'revert' | 'click' | 'beam' | 'pop';

export class AudioCues {
  private ctx: AudioContext | null = null;
  enabled = true;

  /** Must be called from a user gesture on iOS. */
  unlock(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return;
    }
    const AC = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return;
    this.ctx = new AC();
  }

  /** `param` is cue-specific: creak pitch progress 0..1; thud intensity 0..1. */
  play(cue: Cue, param = 0): void {
    if (!this.enabled || !this.ctx) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;
    const gain = ctx.createGain();
    gain.connect(ctx.destination);
    const osc = ctx.createOscillator();
    osc.connect(gain);
    switch (cue) {
      case 'wobble':
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(220, t);
        osc.frequency.linearRampToValueAtTime(160, t + 0.12);
        osc.frequency.linearRampToValueAtTime(220, t + 0.24);
        gain.gain.setValueAtTime(0.18, t);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
        osc.start(t);
        osc.stop(t + 0.3);
        break;
      case 'creak': {
        // Short creak whose pitch rises with the wobble progress (0..1).
        const f = 170 + 260 * param;
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(f, t);
        osc.frequency.linearRampToValueAtTime(f * 1.25, t + 0.05);
        osc.frequency.linearRampToValueAtTime(f * 0.9, t + 0.09);
        gain.gain.setValueAtTime(0.05 + 0.06 * param, t);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.1);
        osc.start(t);
        osc.stop(t + 0.1);
        break;
      }
      case 'fire':
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(900, t);
        osc.frequency.exponentialRampToValueAtTime(120, t + 0.18);
        gain.gain.setValueAtTime(0.15, t);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
        osc.start(t);
        osc.stop(t + 0.2);
        break;
      case 'bonus': {
        osc.type = 'square';
        const notes = [523, 659, 784, 1047];
        notes.forEach((f, i) => osc.frequency.setValueAtTime(f, t + i * 0.09));
        gain.gain.setValueAtTime(0.12, t);
        gain.gain.setValueAtTime(0.12, t + 0.34);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
        osc.start(t);
        osc.stop(t + 0.5);
        break;
      }
      case 'thud': {
        // Low impact; intensity scales with fall distance.
        const k = 0.5 + 0.5 * Math.min(1, param);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(110 * k + 40, t);
        osc.frequency.exponentialRampToValueAtTime(35, t + 0.18);
        gain.gain.setValueAtTime(0.3 * k, t);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.22);
        osc.start(t);
        osc.stop(t + 0.22);
        break;
      }
      case 'coins': {
        osc.type = 'triangle';
        const notes = [1319, 1568, 1760, 2093, 1760];
        notes.forEach((f, i) => osc.frequency.setValueAtTime(f, t + i * 0.05));
        gain.gain.setValueAtTime(0.1, t);
        gain.gain.setValueAtTime(0.08, t + 0.2);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
        osc.start(t);
        osc.stop(t + 0.4);
        break;
      }
      case 'bonk':
        osc.type = 'square';
        osc.frequency.setValueAtTime(320, t);
        osc.frequency.exponentialRampToValueAtTime(90, t + 0.25);
        gain.gain.setValueAtTime(0.14, t);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
        osc.start(t);
        osc.stop(t + 0.3);
        break;
      case 'click':
        // Dry click: a short wooden tick, the lantern has nothing to give.
        osc.type = 'square';
        osc.frequency.setValueAtTime(220, t);
        gain.gain.setValueAtTime(0.06, t);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.04);
        osc.start(t);
        osc.stop(t + 0.04);
        break;
      case 'beam':
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(220, t);
        osc.frequency.exponentialRampToValueAtTime(1400, t + 0.25);
        osc.frequency.exponentialRampToValueAtTime(300, t + 0.6);
        gain.gain.setValueAtTime(0.18, t);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.65);
        osc.start(t);
        osc.stop(t + 0.65);
        break;
      case 'pop':
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(700, t);
        osc.frequency.exponentialRampToValueAtTime(180, t + 0.12);
        gain.gain.setValueAtTime(0.12, t);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
        osc.start(t);
        osc.stop(t + 0.15);
        break;
      case 'ping':
        osc.type = 'sine';
        osc.frequency.setValueAtTime(1046, t);
        gain.gain.setValueAtTime(0.08, t);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
        osc.start(t);
        osc.stop(t + 0.25);
        break;
      case 'gem': {
        // Pitch climbs with the chain so an octave run sings upward.
        const f = 660 * Math.pow(2, param * 0.9);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(f, t);
        gain.gain.setValueAtTime(0.09, t);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.14);
        osc.start(t);
        osc.stop(t + 0.14);
        break;
      }
      case 'step':
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(140, t);
        osc.frequency.exponentialRampToValueAtTime(70, t + 0.05);
        gain.gain.setValueAtTime(0.05, t);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.06);
        osc.start(t);
        osc.stop(t + 0.06);
        break;
      case 'mutate':
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(200, t);
        osc.frequency.exponentialRampToValueAtTime(900, t + 0.2);
        gain.gain.setValueAtTime(0.1, t);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
        osc.start(t);
        osc.stop(t + 0.25);
        break;
      case 'revert':
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(900, t);
        osc.frequency.exponentialRampToValueAtTime(200, t + 0.2);
        gain.gain.setValueAtTime(0.08, t);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
        osc.start(t);
        osc.stop(t + 0.25);
        break;
      case 'bonusEnd': {
        osc.type = 'square';
        const notes = [1047, 784, 659, 523];
        notes.forEach((f, i) => osc.frequency.setValueAtTime(f, t + i * 0.08));
        gain.gain.setValueAtTime(0.1, t);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
        osc.start(t);
        osc.stop(t + 0.4);
        break;
      }
    }
  }
}

export class Haptics {
  enabled = true;
  vibrate(pattern: number | number[]): void {
    if (!this.enabled) return;
    try {
      navigator.vibrate?.(pattern);
    } catch {
      /* unsupported */
    }
  }
}
